import { useEffect, useState } from 'react'
import { Box, Button, IconButton, List, ListItem } from '@material-ui/core'
import ListSubheader from '@material-ui/core/ListSubheader'
import ListItemText from '@material-ui/core/ListItemText'
import { Add as AddIcon } from '@material-ui/icons'
import { useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
import { Role, TrackingRows } from '@types'
import { patchFiles } from '../state/trackingSlice2'
import EditFileCollapse from './EditFileCollapse'

interface Props {
  row: TrackingRows
  role: Role
  isAddEnable: boolean
  onCancel?: () => void
  onSaved?: () => void
  showActions?: boolean
}

const DocumentsPanel: React.FC<Props> = ({ row, role, isAddEnable, onCancel, onSaved, showActions = true }) => {
  const [files, setFiles] = useState(row.files || [])
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    setFiles(row.files || [])
  }, [row])

  const removeFile = (i: number) => {
    const filesCopy = [...files]
    filesCopy.splice(i, 1)
    setFiles(filesCopy)
  }

  const saveFiles = () => {
    dispatch(
      patchFiles({
        files: files.map(file => ({ name: file.name, link: file.link })),
        userId: row.id ?? ''
      })
    )
    enqueueSnackbar('Cambios guardados', { variant: 'success' })
    onSaved?.()
  }

  const cancelEdit = () => {
    setFiles(row.files || [])
    onCancel?.()
  }

  const canEdit = showActions && isAddEnable
  const canViewBankDocument =
    role === Role.admin || role === Role.ejecutivo || role === Role.agente || role === Role.gerente
  const canViewCompanyDocs = (role === Role.operaciones || role === Role.admin) && row.companyDocs
  const canViewFiles =
    role === Role.admin ||
    role === Role.asesor ||
    role === Role.ejecutivo ||
    role === Role.operaciones ||
    role === Role.agente ||
    role === Role.gerente

  return (
    <>
      <Box fontSize="18px" fontWeight="bold" margin="10px 0 10px 0" color="#fc6404">
        Documentos:
      </Box>
      <Box bgcolor="white" padding="10px">
        <List
          component="nav"
          aria-labelledby="documents-list-subheader"
          subheader={
            <ListSubheader
              component="div"
              style={{
                color: '#6c757d',
                borderBottom: '2px solid #ff6900',
                height: '40px',
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center'
              }}
              id="documents-list-subheader"
            >
              {canEdit ? (
                <IconButton
                  style={{
                    color: '#ff6900',
                    width: '10px',
                    height: '10px',
                    margin: '0 10px'
                  }}
                  onClick={() => {
                    setFiles(prev => [...prev, { _id: `new-${prev.length}`, name: '', link: '' }])
                  }}
                >
                  <AddIcon />
                </IconButton>
              ) : (
                ''
              )}
            </ListSubheader>
          }
          style={{
            width: '100%'
          }}
        >
          <div
            style={{
              width: '100%',
              height: 'auto',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'start',
              maxHeight: '80vh',
              overflow: 'hidden auto'
            }}
          >
            {canViewBankDocument ? (
              <ListItem>
                <Box lineHeight="normal" width="100%">
                  <a className="anchor-bar" href={row.bankDocument} target="_blank" rel="noreferrer">
                    {'[Extracto]'}
                  </a>
                </Box>
              </ListItem>
            ) : null}

            {canViewCompanyDocs ? (
              <ListItem>
                <Box lineHeight="normal" width="100%">
                  <a className="anchor-bar" href={row.companyDocs} target="_blank" rel="noreferrer">
                    Documentos escaneados
                  </a>
                </Box>
              </ListItem>
            ) : null}

            {files.length === 0 && (
              <ListItem
                style={{
                  width: '100%'
                }}
              >
                <ListItemText style={{ color: '#6c757d' }} primary="Sin documentos" />
              </ListItem>
            )}

            {canViewFiles
              ? files.map((file, i) => (
                  <ListItem
                    key={`${file._id}-${i}`}
                    style={{
                      flexDirection: 'column',
                      width: '100%'
                    }}
                  >
                    <EditFileCollapse
                      isEditEnable={canEdit}
                      name={file.name}
                      link={file.link}
                      isNew={file._id?.includes('new-')}
                      setName={name =>
                        setFiles(prev => prev.map(item => (item._id === file._id ? { ...item, name } : item)))
                      }
                      setLink={link =>
                        setFiles(prev => prev.map(item => (item._id === file._id ? { ...item, link } : item)))
                      }
                      onDelete={() => removeFile(i)}
                    />
                  </ListItem>
                ))
              : null}

            {showActions ? (
              <Box
                display="grid"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gridTemplateColumns="35% 35%"
                gridGap="5%"
                gridTemplateRows="100%"
                marginTop="20px"
                marginBottom="10px"
              >
                <Button type="button" variant="contained" color="inherit" onClick={cancelEdit}>
                  Cancelar
                </Button>
                <Button type="button" variant="contained" color="primary" onClick={saveFiles}>
                  Guardar
                </Button>
              </Box>
            ) : null}
          </div>
        </List>
      </Box>
    </>
  )
}

export default DocumentsPanel
